import * as React from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Sparkles } from 'lucide-react';
import { useT } from '../lib/i18n.js';
import { errorMessage } from '../lib/properties.js';
import {
  createDemoCase,
  getGuide,
  GUIDE_QUERY_KEY,
  guideStepTarget,
  removeDemoCases,
  setGuideHidden,
  type GuideDto,
  type GuideStepId,
} from '../lib/guide-api.js';
import { V4Bar, V4Pill, V4State } from './ux-v4.js';

/**
 * Krok PRU1 — KARTA „CO SI VYZKOUŠET" NA DNESKU (zadani bod 1, ne modal).
 *
 * Karta NIC neodskrtava sama: stav kroku prichazi ze serveru, ktery ho
 * odvozuje ze skutecne provedenych akci makléře (`apps/api/src/guide`).
 * Tady se jen kresli a nabizi cesta („Ukaž mi" = prechod + jemne
 * zvyrazneni ciloveho mista, `guide-highlight.tsx`).
 *
 * UX v4: jedno primarni tlacitko na obrazovku patri karte „Další krok" —
 * proto tu jsou jen tlacitka tonovana a textova.
 */
export function GuideCard({
  fallbackCaseId,
}: {
  /** Zakazka pro „Ukaž mi", kdyz makler nema ukazkovou (nejnoveji zmenena). */
  readonly fallbackCaseId: string | null;
}): React.JSX.Element | null {
  const t = useT();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const guide = useQuery({ queryKey: GUIDE_QUERY_KEY, queryFn: getGuide });
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [notice, setNotice] = React.useState<string | null>(null);

  const data = guide.data;
  if (data === undefined || !data.visible) return null;

  async function run(action: () => Promise<unknown>, after?: () => void): Promise<void> {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      await action();
      after?.();
      await queryClient.invalidateQueries({ queryKey: GUIDE_QUERY_KEY });
      await queryClient.invalidateQueries({ queryKey: ['dnesek'] });
    } catch (e) {
      setError(t('webAdmin.guide.actionError', { error: errorMessage(e) }));
    } finally {
      setBusy(false);
    }
  }

  function go(href: string): void {
    void navigate({ href });
  }

  const caseId = data.demo?.caseId ?? fallbackCaseId;
  const percent = data.requiredCount === 0 ? 0 : Math.round((data.doneCount / data.requiredCount) * 100);
  const progress = t('webAdmin.guide.progress', { done: data.doneCount, total: data.requiredCount });

  return (
    <section
      className="rh-v4-group rh-v4-guide"
      data-testid="guide-card"
      data-completed={data.completed}
      aria-labelledby="guide-card-title"
    >
      <div className="rh-v4-guide__head">
        <span className="rh-v4-ico rh-v4-ico--sm" aria-hidden="true">
          <Sparkles size={16} strokeWidth={2.2} />
        </span>
        <span className="rh-v4-guide__headtext">
          <h2 className="rh-v4-guide__title" id="guide-card-title">
            {data.completed ? t('webAdmin.guide.completedTitle') : t('webAdmin.guide.title')}
          </h2>
          <span className="rh-v4-guide__sub" data-testid="guide-progress">
            {progress}
          </span>
        </span>
      </div>
      <div className="rh-v4-guide__bar">
        <V4Bar
          percent={percent}
          size="sm"
          tone={data.completed ? 'done' : 'accent'}
          label={`${t('webAdmin.guide.progressLabel')}: ${progress}`}
          testId="guide-progress-bar"
        />
      </div>
      <p className="rh-v4-guide__intro">
        {data.completed ? t('webAdmin.guide.completedBody') : t('webAdmin.guide.intro')}
      </p>

      {data.completed ? null : (
        <ol className="rh-v4-guide__steps">
          {data.steps.map((step) => (
            <GuideStepRow
              key={step.id}
              step={step}
              target={guideStepTarget(step.id, { caseId })}
              onGo={go}
            />
          ))}
        </ol>
      )}

      {data.completed ? null : (
        <DemoBlock
          demo={data.demo}
          busy={busy}
          onCreate={() =>
            void run(async () => {
              const created = await createDemoCase();
              go(`/zakazky/${created.caseId}`);
            })
          }
          onOpen={(id) => go(`/zakazky/${id}`)}
          onRemove={() => {
            if (typeof window !== 'undefined' && !window.confirm(t('webAdmin.guide.demo.removeConfirm'))) return;
            void run(removeDemoCases, () => setNotice(t('webAdmin.guide.demo.removed')));
          }}
        />
      )}

      {notice !== null ? (
        <p className="rh-v4-guide__note" role="status" data-testid="guide-notice">
          {notice}
        </p>
      ) : null}
      {error !== null ? (
        <p className="rh-a2-error" role="alert" data-testid="guide-error">
          {error}
        </p>
      ) : null}

      <div className="rh-v4-guide__foot">
        <button
          type="button"
          className="rh-v4-btn rh-v4-btn--plain"
          data-testid="guide-hide"
          disabled={busy}
          onClick={() => void run(() => setGuideHidden(true))}
        >
          {t('webAdmin.guide.hide')}
        </button>
      </div>
    </section>
  );
}

function GuideStepRow({
  step,
  target,
  onGo,
}: {
  readonly step: GuideDto['steps'][number];
  readonly target: string;
  readonly onGo: (href: string) => void;
}): React.JSX.Element {
  const t = useT();
  const id: GuideStepId = step.id;
  return (
    <li
      className={`rh-v4-guide__step${step.done ? ' is-done' : ''}`}
      data-testid={`guide-step-${id}`}
      data-done={step.done}
    >
      <V4State state={step.done ? 'done' : 'later'} />
      <span className="rh-v4-guide__steptext">
        <span className="rh-v4-guide__steptitle">
          {t(`webAdmin.guide.steps.${id}.title`)}
          {step.optional ? (
            <>
              {' '}
              <V4Pill>{t('webAdmin.guide.optional')}</V4Pill>
            </>
          ) : null}
        </span>
        <span className="rh-v4-guide__stepwhy">{t(`webAdmin.guide.steps.${id}.why`)}</span>
      </span>
      {step.done ? (
        <span className="rh-v4-guide__done" data-testid={`guide-step-${id}-done`}>
          {t('webAdmin.guide.done')}
        </span>
      ) : (
        <a
          href={target}
          className="rh-v4-btn rh-v4-btn--chip"
          data-testid={`guide-step-${id}-show`}
          onClick={(event) => {
            event.preventDefault();
            onGo(target);
          }}
        >
          {t('webAdmin.guide.showMe')}
        </a>
      )}
    </li>
  );
}

function DemoBlock({
  demo,
  busy,
  onCreate,
  onOpen,
  onRemove,
}: {
  readonly demo: GuideDto['demo'];
  readonly busy: boolean;
  readonly onCreate: () => void;
  readonly onOpen: (caseId: string) => void;
  readonly onRemove: () => void;
}): React.JSX.Element {
  const t = useT();
  if (demo === null) {
    return (
      <div className="rh-v4-guide__demo" data-testid="guide-demo">
        <button
          type="button"
          className="rh-v4-btn rh-v4-btn--tinted rh-v4-btn--block"
          data-testid="guide-demo-create"
          disabled={busy}
          onClick={onCreate}
        >
          {t('webAdmin.guide.demo.try')}
        </button>
        <span className="rh-v4-guide__hint">{t('webAdmin.guide.demo.hint')}</span>
      </div>
    );
  }
  return (
    <div className="rh-v4-guide__demo" data-testid="guide-demo">
      <span className="rh-v4-guide__demotitle">
        <V4Pill tone="accent">{t('webAdmin.guide.demo.badge')}</V4Pill> {demo.title}
      </span>
      <span className="rh-v4-guide__demoacts">
        <button
          type="button"
          className="rh-v4-btn rh-v4-btn--tinted"
          data-testid="guide-demo-open"
          onClick={() => onOpen(demo.caseId)}
        >
          {t('webAdmin.guide.demo.open')}
        </button>
        <button
          type="button"
          className="rh-v4-btn rh-v4-btn--plain"
          data-testid="guide-demo-remove"
          disabled={busy}
          onClick={onRemove}
        >
          {t('webAdmin.guide.demo.remove')}
        </button>
      </span>
    </div>
  );
}
